/**
 * Barbridge public API client.
 *
 * Reads the API base URL from /config.json (written by the deploy workflow at
 * runtime) and falls back to the dev endpoint when that file is absent.
 *
 * Public endpoints (no auth):
 *   GET  /c/{venue_short_name}         — venue branding + active offer
 *   POST /c/{venue_short_name}/signup  — Phase A: submit form, send SMS
 *   GET  /claim/{token}                — Phase B: resolve magic link
 *   GET  /card/{signed_token}          — card page data (browser) or .pkpass (iOS)
 */

const DEV_API_BASE = '/api';

let cachedApiBase: string | null = null;

async function getApiBase(): Promise<string> {
  if (cachedApiBase !== null) return cachedApiBase;
  try {
    const resp = await fetch('/config.json', { cache: 'no-store' });
    if (resp.ok) {
      const cfg = (await resp.json()) as { apiBaseUrl?: string };
      if (cfg.apiBaseUrl) {
        cachedApiBase = cfg.apiBaseUrl;
        return cachedApiBase;
      }
    }
  } catch {
    // fall through to dev default
  }
  cachedApiBase = DEV_API_BASE;
  return cachedApiBase;
}

// ── Domain types ──────────────────────────────────────────────────────────────

export type VenueInfo = {
  name: string;
  logo_url: string | null;
  background_color: string | null;
  foreground_color: string | null;
};

export type OfferInfo = {
  title: string;
  body: string;
  terms: string | null;
};

export type VenuePage = {
  venue: VenueInfo;
  offer: OfferInfo | null;
};

export type SignupData = {
  phone: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  birthdate: string;
  delivery_consent: boolean;
  marketing_consent: boolean;
};

export type SignupResult = {
  status: 'pending_verification';
  is_duplicate: boolean;
  message: string;
};

export type ClaimResult = {
  offer: OfferInfo;
  venue: VenueInfo;
  card_url: string;
  // Direct link to the API endpoint that serves the .pkpass, so the
  // add-to-wallet button adds the pass in one tap (vs. routing to card_url).
  card_download_url: string;
  customer: { first_name: string };
  instructions: string;
  sms_backup_note: string;
};

export type CardResult = {
  offer: OfferInfo | null;
  venue: VenueInfo | null;
  card_download_url: string;
  instructions: string;
};

// ── Error type ────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Core fetch helper ─────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const base = await getApiBase();
  const resp = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  const json = (await resp.json()) as { error?: string; details?: unknown } & T;
  if (!resp.ok) {
    throw new ApiError(resp.status, json.error ?? 'Unknown error', json.details);
  }
  return json;
}

// ── Public API calls ──────────────────────────────────────────────────────────

/**
 * Fetch venue branding and active offer for the signup form.
 *
 * Returns null on any error except a definitive 404 "Venue not found" from
 * the server — that case throws so the form can show a blocked state. All
 * other failures (network error, endpoint not yet deployed) return null and
 * the form renders without branding.
 */
export async function fetchVenuePage(venueShortName: string): Promise<VenuePage | null> {
  try {
    return await apiFetch<VenuePage>(`/c/${encodeURIComponent(venueShortName)}`);
  } catch (err) {
    if (err instanceof ApiError) {
      // Propagate explicit venue-not-found and account-expired so the form can
      // show the right blocked state. Distinguish from a generic 404 (e.g.
      // route not yet deployed) by checking the error message.
      if (err.status === 404 && err.message === 'Venue not found') throw err;
      if (err.status === 422) throw err;
    }
    console.warn('[barbridge] fetchVenuePage failed — form will render without branding', err);
    return null;
  }
}

export async function submitSignup(
  venueShortName: string,
  data: SignupData,
): Promise<SignupResult> {
  return apiFetch<SignupResult>(`/c/${encodeURIComponent(venueShortName)}/signup`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function claimToken(token: string): Promise<ClaimResult> {
  return apiFetch<ClaimResult>(`/claim/${encodeURIComponent(token)}`);
}

export async function fetchCard(signedToken: string): Promise<CardResult> {
  return apiFetch<CardResult>(`/card/${encodeURIComponent(signedToken)}`);
}

/** For test isolation — resets the cached API base URL. */
export function __resetApiBase(): void {
  cachedApiBase = null;
}
